import type {JSX} from 'react';
import {useMemo, useState} from 'react';

import {Button, DatePicker, SearchBar} from '@/components';
import {Select} from '@/components/common/Input';
import {Filter} from '@/components/Filter';
import type {AfriexQueryParams} from '@/types';
import {AttributionEventType} from '@/types/attribution';

interface AppMetricsFiltersProps {
  currentFilters: AfriexQueryParams;
  filtersToApply: AfriexQueryParams & {events: string[]};
  clearFilters: () => void;
  selectAppliedFilters: (
    name: string,
    value: string | Date | string[] | undefined,
  ) => void;
  selectCurrentFilters: (
    name: string,
    value: string | Date | undefined,
  ) => void;
  /** Returns whether the filters were applied; false when validation rejected them. */
  handleApplyFilters: () => boolean;
  handleAutoComplete: (search: string) => void;
}

export default function AppMetricsFilters({
  filtersToApply,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
  handleAutoComplete,
}: AppMetricsFiltersProps): JSX.Element {
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const allEventTypes = useMemo(() => Object.values(AttributionEventType), []);

  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;

  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleShowFilterChange = (): void => {
    setShowFilters(!showFilters);
  };

  const handleApplyFilter = (): void => {
    // Only close on success. Dismissing the panel after a rejected date range
    // hid the values the user has to correct. See hooks/useFilters.ts.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  const handleClearFilters = (): void => {
    setShowFilters(false);
    clearFilters();
  };

  const eventOptions = Object.values(AttributionEventType).map((type) => ({
    key: type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
    value: type,
  }));

  return (
    <div className="mt-5 w-full">
      <div className="flex items-center justify-between">
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-3/5">
          <SearchBar
            handleSearch={handleAutoComplete}
            handleAutoComplete={handleAutoComplete}
            clearFilters={handleClearFilters}
            placeholder="Search by user ID"
            inputClassName="sm:w-4/6 w-full"
          />
          <div className="flex items-center self-start sm:self-center">
            <Filter
              handleShowFilter={handleShowFilterChange}
              closeFilter={() => setShowFilters(false)}
              label="Filters"
              bodyClassName="w-full md:w-[400px]"
              positionLeft={1}
            >
              {showFilters && (
                <div className="p-2 space-y-4 max-w-4xl mx-auto w-full col-span-full">
                  <div className="">
                    <div className="flex flex-col md:flex-row gap-2">
                      <DatePicker
                        label="From Date"
                        selectedDate={fromDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('fromDate', date)
                        }
                        placeholderText="Select Date"
                      />

                      <DatePicker
                        label="To Date"
                        selectedDate={toDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('toDate', date)
                        }
                        placeholderText="Select Date"
                      />
                    </div>

                    <div className="mt-3">
                      <Select
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          selectAppliedFilters(
                            'events',
                            e.target.value ? [e.target.value] : allEventTypes,
                          )
                        }
                        value={filtersToApply.events?.[0] || ''}
                        name="events"
                        options={eventOptions}
                        placeholder="Select Event Type"
                        label="Filter by Event Type"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleClearFilters}
                      colorScheme="cyan"
                      variant="ghost"
                    >
                      Clear
                    </Button>

                    <Button
                      onClick={handleApplyFilter}
                      colorScheme="cyan"
                      className="ml-3"
                    >
                      Apply Filters
                    </Button>
                  </div>
                </div>
              )}
            </Filter>
          </div>
        </div>
      </div>
    </div>
  );
}
